import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { Button } from '~/elements';
import { TextInput } from 'jl-design-system/components/text-input/TextInput';
import { CurrentLocationButton } from './current-location-button';
import { FormFieldAdapter } from '../form';

import styles from './SearchForm.scss';

export const SearchFormContext = React.createContext(false);

export class SearchForm extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      userSearchQuery: props.storeSearchQuery,
      storeSearchQuery: props.storeSearchQuery,
      submitted: false,
      showInlineError: false,
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.storeSearchQuery && props.storeSearchQuery !== state.storeSearchQuery) {
      const { validate, storeSearchQuery } = props;
      return {
        userSearchQuery: storeSearchQuery,
        storeSearchQuery,
        showInlineError: validate ? !!validate(storeSearchQuery) : !storeSearchQuery.trim(),
      };
    }
    return null;
  }

  onChangeHandler = ({ target: { value } }) => {
    const { transformSearchQuery } = this.props;
    const userSearchQuery = transformSearchQuery ? transformSearchQuery(value) : value;

    this.setState({
      userSearchQuery,
      submitted: false,
      showInlineError: this.hasSearchTermError(userSearchQuery),
    });
  };

  submitHandler = (event) => {
    event.preventDefault();
    const { onSubmitHandler, storeSearchQuery, resetErrorMessagesWith } = this.props;
    const { userSearchQuery } = this.state;

    const searchTerm = (userSearchQuery === null ? storeSearchQuery : userSearchQuery)
      .replace(/\s+/g, ' ')
      .trim();

    const isInputEmpty = !searchTerm;
    if (isInputEmpty) {
      resetErrorMessagesWith([]);
    } else {
      onSubmitHandler(searchTerm);
    }

    this.setState({
      submitted: true,
      showInlineError: this.hasSearchTermError(searchTerm),
    });
  };

  hasSearchTermError = (searchTerm) => {
    const { validate } = this.props;
    return validate ? !!validate(searchTerm) : !searchTerm || !searchTerm.trim();
  };

  searchByCurrentLocation = (event) => {
    event.preventDefault();
    const { onSubmitByLocationHandler } = this.props;

    navigator.geolocation.getCurrentPosition(({ coords: { latitude, longitude } }) => {
      onSubmitByLocationHandler(`${latitude},${longitude}`);
      this.setState({
        storeSearchQuery: null,
        submitted: true,
      });
    });
  };

  onBlurHandler = () => {
    const { storeSearchQuery } = this.props;
    const { userSearchQuery } = this.state;
    const searchTerm = userSearchQuery === null ? storeSearchQuery : userSearchQuery;

    if (this.hasSearchTermError(searchTerm)) {
      this.setState({ showInlineError: true });
    }
  };

  render() {
    const { title, storeSearchQuery, children, isLocationLoading, sensitiveSearchInput } =
      this.props;
    const { userSearchQuery, submitted, showInlineError } = this.state;

    const { searchFormInlineErrorMsg } = this.context;

    const searchTerm = userSearchQuery === null ? storeSearchQuery : userSearchQuery;

    const label = [title];

    label.indexOf = () => 0;

    return (
      <form className={styles.searchForm} onSubmit={this.submitHandler} data-testid="SearchForm">
        {children}
        <div className={styles.searchFormLine}>
          <FormFieldAdapter
            formFieldConfig={{
              component: TextInput,
              label,
              maxLength: 60,
            }}
            meta={{
              error: showInlineError ? searchFormInlineErrorMsg : null,
              submitFailed: submitted && showInlineError,
              touched: true,
            }}
            input={{
              name: 'search',
              value: searchTerm,
              onChange: this.onChangeHandler,
              onBlur: this.onBlurHandler,
              onFocus: () => {},
            }}
            disabled={isLocationLoading}
            sessionCamExclude={sensitiveSearchInput}
            data-testid="FormFieldAdapter"
          />
          <Button
            type="submit"
            view="primary"
            className={styles.searchButton}
            disabled={isLocationLoading}
          >
            Find
          </Button>
        </div>
        {navigator.geolocation && (
          <CurrentLocationButton
            onClick={this.searchByCurrentLocation}
            isLocationLoading={isLocationLoading}
            data-testid="CurrentLocationButton"
          />
        )}
      </form>
    );
  }
}

SearchForm.contextType = SearchFormContext;

SearchForm.propTypes = {
  children: PropTypes.node,
  isLocationLoading: PropTypes.bool.isRequired,
  onSubmitByLocationHandler: PropTypes.func.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  resetErrorMessagesWith: PropTypes.func.isRequired,
  transformSearchQuery: PropTypes.func,
  storeSearchQuery: PropTypes.string,
  title: PropTypes.string,
  sensitiveSearchInput: PropTypes.bool,
  validate: PropTypes.func,
};

SearchForm.defaultProps = {
  children: null,
  storeSearchQuery: '',
  title: '',
  transformSearchQuery: null,
  sensitiveSearchInput: false,
  validate: undefined,
};
